import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import {
  mergeChatPassengerRoomsIntoPrev,
  normalizePassengerFormFromReply,
  resolveCustomerChatTurnReply,
} from "../../../utils/customerChatPassengerForm";
import { deriveChatStage, resolveChatBottomCtaWhenReady } from "../../../utils/customerChatStage";
import {
  mergeLoadMoreIntoQuickReplies,
  sanitizeAssistantLoadMoreHotels,
} from "../../../utils/customerChatLoadMoreSanitize";
import { useScrollToBottom } from "../../../hooks/useScrollToBottom";
import {
  listPublicCustomerChatMessages,
  publicChatSessionStorageKey,
  publicSendCustomerChatTurn,
  publicStartCustomerChat,
} from "../../../services/customerChatPublicService";
import {
  CVAC_EXECUTE_PACKAGE_SEARCH,
  displayUserChatMessage,
} from "../../../utils/customerChatMessageDisplay";
import { normalizeOutgoingChatMessage } from "../../../utils/normalizeOutgoingChatMessage";
import ChildrenAgePicker from "../../../Components/Client/CustomerVacationChat/ChildrenAgePicker";
import CustomerVacationChatDateRangePicker from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatDateRangePicker";
import { filterOutGuidedPrimaryDateModeReplies } from "../../../utils/customerChatGuidedDatesQuickReplies";
import CustomerVacationChatAssistantText from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatAssistantText";
import HotelShortlistCard from "../../../Components/Client/CustomerVacationChat/HotelShortlistCard";
import PackageDetailsChatView, {
  packageDetailsViewToCard,
} from "../../../Components/Client/CustomerVacationChat/PackageDetailsChatView";
import PassengerFormChatView from "../../../Components/Client/CustomerVacationChat/PassengerFormChatView";
import CustomerVacationChatQuickReplyLabel from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatQuickReplyLabel";
import CustomerVacationChatPaymentModal from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatPaymentModal";
import CustomerVacationChatPaymentFallback from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatPaymentFallback";
import { extractVacationPaymentAgentId } from "../../../utils/customerChatPaymentOpen";
import { selectPackageDealForChatContext } from "../../../utils/customerChatPackageProfileNavigation";
import {
  processChatPaymentClientActions,
  processChatPaymentManualOpen,
  onChatPaymentModalClosed,
} from "../../../services/customerChatPaymentOrchestrator";
import {
  captureCvacServerStateFromPayload,
  withCvacServerStateMetadata,
} from "../../../utils/customerChatStateSnapshot";
import CustomerVacationChatHotelFilterBar from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatHotelFilterBar";
import { useCustomerVacationChatHotelFilter } from "../../../hooks/useCustomerVacationChatHotelFilter";
import { resolveAssistantMessageHotelCards } from "../../../utils/customerChatHotelFilter";
import "../../../Components/Client/CustomerVacationChat/CustomerVacationChatWidget.css";
import { CUSTOMER_VACATION_CHAT_WELCOME_TEXT } from "../../../utils/customerVacationChatWelcome";
import {
  CUSTOMER_VACATION_CHAT_OPENING_QUICK_REPLIES,
  CUSTOMER_VACATION_CHAT_OPENING_SECTION_TITLE,
} from "../../../utils/customerVacationChatOpeningQuickReplies";
import "./CustomerChatPublicStartPage.css";

function mapServerMessagesToUi(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((m) => {
    const role = (m.role || "").toLowerCase();
    if (role === "user") {
      return { role: "user", text: m.message_text || "" };
    }
    const toolOut = m.tool_output_json || m.tool_output || {};
    const synthetic = { reply: toolOut };
    const reply = resolveCustomerChatTurnReply(synthetic);
    const { form: passengerFormNorm } = normalizePassengerFormFromReply(synthetic);
    const cards = Array.isArray(reply.cards) ? reply.cards : [];
    const lm = reply.load_more_hotels;
    const lmSan = sanitizeAssistantLoadMoreHotels(cards, lm);
    const rawQr = Array.isArray(reply.quick_replies) ? reply.quick_replies : [];
    const qrs = passengerFormNorm ? [] : mergeLoadMoreIntoQuickReplies(rawQr, lmSan);
    return {
      role: "assistant",
      text: m.message_text || "",
      cards,
      quick_replies: qrs,
      load_more_hotels: lmSan,
      search_results_meta: toolOut.search_results_meta || reply.search_results_meta || null,
      package_details_view: reply.package_details_view || null,
      passenger_form: passengerFormNorm,
      client_actions: Array.isArray(reply.client_actions) ? reply.client_actions : [],
      payment:
        toolOut.payment && typeof toolOut.payment === "object" ? toolOut.payment : null,
      trace_id: m.trace_id ?? reply.trace_id,
    };
  });
}

export default function CustomerChatPublicStartPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    setSelectedResult_resetAllFields,
    setRoomsAndPaxInfo,
    selectedResult,
    roomsAndPaxInfo,
  } = useSelectedResult();
  const ref = (searchParams.get("ref") || "").trim();
  const [sessionBoot, setSessionBoot] = useState(0);

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [chatPaymentModalOpen, setChatPaymentModalOpen] = useState(false);
  const chatPaymentModalOpenRef = useRef(false);

  const cvacServerStateRef = useRef(null);

  useEffect(() => {
    chatPaymentModalOpenRef.current = chatPaymentModalOpen;
  }, [chatPaymentModalOpen]);

  useEffect(() => {
    if (!chatPaymentModalOpen) {
      onChatPaymentModalClosed();
    }
  }, [chatPaymentModalOpen]);
  const {
    resetHotelFilter,
    onServerStateCaptured,
    selectedHotelFilter,
    setSelectedHotelFilter,
    availableHotelFilters,
    hotelFilterAnchorMessageIndex,
    showHotelFilterBar,
    statusLine,
    consolidatedHotelFilterCards,
  } = useCustomerVacationChatHotelFilter({
    cvacServerStateRef,
    messages,
  });
  const chatStageInfo = useMemo(() => deriveChatStage(messages), [messages]);
  const bottomCta = useMemo(
    () =>
      resolveChatBottomCtaWhenReady(messages, chatStageInfo, {
        loading,
        bootLoading,
      }),
    [messages, chatStageInfo, loading, bootLoading]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const cta = bottomCta;
    // eslint-disable-next-line no-console
    console.info("[cvac] chat_stage_audit", {
      CURRENT_CHAT_STAGE: chatStageInfo.stage,
      ACTIVE_BOTTOM_CTA: cta?.kind === "none" ? null : cta?.kind,
      CTA_SOURCE_MESSAGE_ID: cta?.sourceMessageIndex,
      CTA_TRACE_ID: cta?.traceId,
      CTA_LABEL: cta?.kind !== "none" ? cta?.label : undefined,
      loading_turn: loading,
    });
  }, [chatStageInfo.stage, bottomCta, loading]);

  const scrollEndRef = useScrollToBottom(
    messages,
    loading,
    bootLoading,
    Boolean(ref) && !bootError,
    sessionBoot
  );

  const shellRef = useRef(null);

  useEffect(() => {
    if (!ref) {
      setBootError("הקישור חסר או אינו תקין. בקשו מהסוכן קישור מעודכן.");
      setBootLoading(false);
      return;
    }

    let cancelled = false;
    const storageKey = publicChatSessionStorageKey(ref);

    (async () => {
      const t0 = Date.now();
      setBootLoading(true);
      setBootError(null);
      try {
        let sid = null;
        try {
          sid = window.sessionStorage.getItem(storageKey);
        } catch {
          sid = null;
        }

        if (sid) {
          try {
            const msgRes = await listPublicCustomerChatMessages(sid, 200);
            if (!cancelled && msgRes?.status === "ok") {
              setSessionId(sid);
              setMessages(mapServerMessagesToUi(msgRes.payload?.messages));
              setBootLoading(false);
              return;
            }
          } catch {
            try {
              window.sessionStorage.removeItem(storageKey);
            } catch {
              /* ignore */
            }
          }
        }

        const startRes = await publicStartCustomerChat({ ref, locale: "he" });
        const newSid = startRes?.payload?.session_id;
        if (!newSid) throw new Error("no_session");
        if (!cancelled) {
          try {
            window.sessionStorage.setItem(storageKey, newSid);
          } catch {
            /* ignore */
          }
          setSessionId(newSid);
          const msgRes = await listPublicCustomerChatMessages(newSid, 200);
          if (!cancelled && msgRes?.status === "ok") {
            setMessages(mapServerMessagesToUi(msgRes.payload?.messages));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setBootError(
            e?.message && e.message !== "public_chat_start_failed"
              ? e.message
              : "לא ניתן לפתוח את הצ'אט. בדקו את הקישור או נסו שוב מאוחר יותר."
          );
        }
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ref, sessionBoot]);

  const handleNewConversation = useCallback(() => {
    try {
      window.sessionStorage.removeItem(publicChatSessionStorageKey(ref));
    } catch {
      /* ignore */
    }
    setSessionId(null);
    setMessages([]);
    setInput("");
    setLoading(false);
    setBootError(null);
    chatPaymentModalOpenRef.current = false;
    setChatPaymentModalOpen(false);
    onChatPaymentModalClosed();
    cvacServerStateRef.current = null;
    resetHotelFilter();
    setSessionBoot((n) => n + 1);
  }, [ref, resetHotelFilter]);

  const appendAssistantPayload = useCallback((payload) => {
    const envelope = payload;
    captureCvacServerStateFromPayload(envelope, cvacServerStateRef);
    onServerStateCaptured(envelope);
    const reply = resolveCustomerChatTurnReply(envelope);
    const { form: passengerFormNorm } = normalizePassengerFormFromReply(envelope);
    const hasPassengerForm = Boolean(passengerFormNorm);
    const rawQr = reply.quick_replies || [];
    setMessages((prev) => {
      const cardsOut = Array.isArray(reply.cards) ? reply.cards : [];
      const loadMoreSanitized = sanitizeAssistantLoadMoreHotels(cardsOut, reply.load_more_hotels);
      const quickWithLoadMore = hasPassengerForm
        ? []
        : mergeLoadMoreIntoQuickReplies(rawQr, loadMoreSanitized);
      const base = hasPassengerForm
        ? prev.map((m) =>
            m.role === "assistant" && m.passenger_form
              ? { ...m, passenger_form: null }
              : m
          )
        : prev;
      return [
        ...base,
        {
          role: "assistant",
          text: reply.text || "",
          cards: cardsOut,
          quick_replies: quickWithLoadMore,
          next_expected_input: reply.next_expected_input,
          children_age_picker: reply.children_age_picker || null,
          date_range_picker: reply.date_range_picker || null,
          load_more_hotels: loadMoreSanitized,
          search_results_meta: reply.search_results_meta || null,
          next_action: reply.next_action || null,
          package_details_view: reply.package_details_view || null,
          passenger_form: passengerFormNorm,
          client_actions: Array.isArray(reply.client_actions) ? reply.client_actions : [],
          payment:
            reply.payment && typeof reply.payment === "object" ? reply.payment : null,
          trace_id: envelope?.trace_id ?? envelope?.payload?.trace_id,
        },
      ];
    });
    const traceId = envelope?.trace_id ?? envelope?.payload?.trace_id;
    const pr = processChatPaymentClientActions({
      reply,
      sessionId,
      agentId: null,
      selectedResult,
      roomsAndPaxInfo,
      modalOpenRef: chatPaymentModalOpenRef,
      traceId,
      onOpenModal: () => setChatPaymentModalOpen(true),
    });
    if (!pr.ok) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            pr.error_code === "PRICE_MISMATCH"
              ? "אי-התאמה בין המחיר שבחרתם לבין הנתונים במערכת. בחרו שוב מהרשימה או פנו לנציג."
              : "לא ניתן לפתוח תשלום — חסר מחיר מאושר. בחרו שוב מהרשימה או פנו לנציג.",
          cards: [],
          quick_replies: [],
        },
      ]);
    }
  }, [onServerStateCaptured, sessionId, selectedResult, roomsAndPaxInfo]);

  const triggerDeferredPackageSearch = useCallback(
    async (sid) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", searchLoading: true, text: "מחפש חבילות מתאימות…" },
      ]);
      setLoading(true);
      try {
        const res = await publicSendCustomerChatTurn(
          sid,
          CVAC_EXECUTE_PACKAGE_SEARCH,
          withCvacServerStateMetadata({}, cvacServerStateRef)
        );
        setMessages((prev) => prev.filter((m) => !m.searchLoading));
        appendAssistantPayload(res.payload);
      } catch (e) {
        setMessages((prev) => prev.filter((m) => !m.searchLoading));
        const fromPayload =
          e?.customerChatPublicPayload?.message ||
          e?.response?.data?.message ||
          (typeof e?.message === "string" ? e.message : "");
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          (typeof fromPayload === "string" && /timeout/i.test(fromPayload));
        const errText =
          (fromPayload && String(fromPayload).trim()) ||
          (isTimeout
            ? "החיפוש לוקח זמן רב או שהשרת לא הגיב בזמן. נסו שוב בעוד רגע או פנו לסוכן."
            : "לא הצלחנו לקבל תשובה כרגע. נסו שוב בעוד רגע.");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: errText, cards: [], quick_replies: [] },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [appendAssistantPayload]
  );

  const sendPassengerControlTurn = useCallback(
    async ({ messageText, metadata, userDisplayText }) => {
      if (!sessionId) return;
      setLoading(true);
      if (userDisplayText) {
        setMessages((prev) => [...prev, { role: "user", text: userDisplayText }]);
      }
      try {
        const res = await publicSendCustomerChatTurn(sessionId, messageText, metadata || {});
        appendAssistantPayload(res.payload);
        const reply = resolveCustomerChatTurnReply(res.payload);
        const saved = reply?.passenger_form_saved;
        if (saved?.rooms) {
          setRoomsAndPaxInfo((prev) => mergeChatPassengerRoomsIntoPrev(saved.rooms, prev), false);
          setMessages((prev) =>
            prev.map((m) =>
              m.role === "assistant" && m.passenger_form
                ? { ...m, passenger_form: null }
                : m
            )
          );
        }
        if (reply?.next_action === "searching") {
          await triggerDeferredPackageSearch(sessionId);
        }
      } catch (e) {
        const fromPayload =
          e?.customerChatPublicPayload?.message ||
          e?.response?.data?.message ||
          (typeof e?.message === "string" ? e.message : "");
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          (typeof fromPayload === "string" && /timeout/i.test(fromPayload));
        const errText =
          (fromPayload && String(fromPayload).trim()) ||
          (isTimeout
            ? "החיפוש לוקח זמן רב או שהשרת לא הגיב בזמן. נסו שוב בעוד רגע או פנו לסוכן."
            : "לא הצלחנו לקבל תשובה כרגע. נסו שוב בעוד רגע.");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: errText, cards: [], quick_replies: [] },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [sessionId, appendAssistantPayload, triggerDeferredPackageSearch, setRoomsAndPaxInfo]
  );

  const sendText = async (text, metadata = {}) => {
    const trimmed = normalizeOutgoingChatMessage(text);
    if (!trimmed || loading || !sessionId) return;
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setInput("");
    setLoading(true);
    try {
      const res = await publicSendCustomerChatTurn(
        sessionId,
        trimmed,
        withCvacServerStateMetadata(metadata, cvacServerStateRef)
      );
      appendAssistantPayload(res.payload);
      if (res.payload?.reply?.next_action === "searching") {
        await triggerDeferredPackageSearch(sessionId);
      }
    } catch (e) {
      const fromPayload =
        e?.customerChatPublicPayload?.message ||
        e?.response?.data?.message ||
        (typeof e?.message === "string" ? e.message : "");
      const isTimeout =
        e?.code === "ECONNABORTED" ||
        (typeof fromPayload === "string" && /timeout/i.test(fromPayload));
      const text =
        (fromPayload && String(fromPayload).trim()) ||
        (isTimeout
          ? "החיפוש לוקח זמן רב או שהשרת לא הגיב בזמן. נסו שוב בעוד רגע או פנו לסוכן."
          : "לא הצלחנו לקבל תשובה כרגע. נסו שוב בעוד רגע.");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text,
          cards: [],
          quick_replies: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onQuickReply = (qr) => {
    const v = qr?.value || qr?.label;
    if (!v) return;
    if (v === "__nav__/contact" || v === "/contact") {
      navigate("/contact");
      return;
    }
    void sendText(v);
  };

  const sendFromCard = (text, metadata) => {
    void sendText(text, metadata || {});
  };

  const sendOpeningQuickReply = async (label, quickKey) => {
    if (loading || bootLoading || !sessionId) return;
    setMessages((prev) => [...prev, { role: "user", text: label }]);
    setLoading(true);
    try {
      const res = await publicSendCustomerChatTurn(
        sessionId,
        label,
        withCvacServerStateMetadata({ cvac_opening_quick: quickKey }, cvacServerStateRef)
      );
      appendAssistantPayload(res.payload);
      if (res.payload?.reply?.next_action === "searching") {
        await triggerDeferredPackageSearch(sessionId);
      }
    } catch (e) {
      const fromPayload =
        e?.customerChatPublicPayload?.message ||
        e?.response?.data?.message ||
        (typeof e?.message === "string" ? e.message : "");
      const isTimeout =
        e?.code === "ECONNABORTED" ||
        (typeof fromPayload === "string" && /timeout/i.test(fromPayload));
      const errText =
        (fromPayload && String(fromPayload).trim()) ||
        (isTimeout
          ? "החיפוש לוקח זמן רב או שהשרת לא הגיב בזמן. נסו שוב בעוד רגע או פנו לסוכן."
          : "לא הצלחנו לקבל תשובה כרגע. נסו שוב בעוד רגע.");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: errText, cards: [], quick_replies: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHotelFromCard = (rank, card) => {
    if (!selectPackageDealForChatContext(card, setSelectedResult_resetAllFields)) return;
    if (!sessionId) return;
    const text = `אני בוחר באפשרות ${rank}`;
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);
    void (async () => {
      try {
        const res = await publicSendCustomerChatTurn(
          sessionId,
          text,
          withCvacServerStateMetadata({}, cvacServerStateRef)
        );
        appendAssistantPayload(res.payload);
        if (res.payload?.reply?.next_action === "searching") {
          await triggerDeferredPackageSearch(sessionId);
        }
      } catch (e) {
        const fromPayload =
          e?.customerChatPublicPayload?.message ||
          e?.response?.data?.message ||
          (typeof e?.message === "string" ? e.message : "");
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          (typeof fromPayload === "string" && /timeout/i.test(fromPayload));
        const errText =
          (fromPayload && String(fromPayload).trim()) ||
          (isTimeout
            ? "החיפוש לוקח זמן רב או שהשרת לא הגיב בזמן. נסו שוב בעוד רגע או פנו לסוכן."
            : "לא הצלחנו לקבל תשובה כרגע. נסו שוב בעוד רגע.");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: errText,
            cards: [],
            quick_replies: [],
          },
        ]);
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div ref={shellRef} className="hr-cc-pub" lang="he" dir="rtl">
      <header className="hr-cc-pub__header">
        <div className="hr-cc-pub__header-row">
          <h1 className="hr-cc-pub__title">שיחה עם הסוכן</h1>
          <button
            type="button"
            className="hr-cvac__new-chat"
            aria-label="שיחה חדשה"
            disabled={bootLoading || !ref || bootError}
            onClick={handleNewConversation}
          >
            שיחה חדשה
          </button>
        </div>
        <p className="hr-cc-pub__sub">
          חיפוש חבילה — עוזר HotelsRiver. כתבו יעד, תאריכים ומספר נוסעים.
        </p>
      </header>

      {bootError ? <div className="hr-cc-pub__err">{bootError}</div> : null}

      <div className="hr-cc-pub__messages">
        {bootLoading ? (
          <div className="hr-cc-pub__loading">פותחים שיחה…</div>
        ) : null}
        {!bootLoading &&
        !bootError &&
        sessionId &&
        messages.length === 0 ? (
          <>
            <div className="hr-cc-pub__welcome hr-cc-pub__bubble hr-cc-pub__bubble--assistant">
              {CUSTOMER_VACATION_CHAT_WELCOME_TEXT}
            </div>
            <div className="hr-cvac__opening-quick-title hr-cc-pub__opening-quick-title">
              {CUSTOMER_VACATION_CHAT_OPENING_SECTION_TITLE}
            </div>
            <div
              className="hr-cvac__opening-quick hr-cc-pub__opening-quick"
              role="group"
              aria-label="בחירה מהירה"
            >
              {CUSTOMER_VACATION_CHAT_OPENING_QUICK_REPLIES.map((qr) => (
                <button
                  key={qr.quickKey}
                  type="button"
                  className="hr-cvac__opening-quick-btn"
                  disabled={loading}
                  onClick={() => void sendOpeningQuickReply(qr.label, qr.quickKey)}
                >
                  {qr.label}
                </button>
              ))}
            </div>
          </>
        ) : null}
        {messages.map((m, i) => {
          const rawBubbleQr = Array.isArray(m.quick_replies) ? m.quick_replies : [];
          const bubbleQuickReplies =
            m.role === "assistant" &&
            m.date_range_picker &&
            i === messages.length - 1
              ? filterOutGuidedPrimaryDateModeReplies(rawBubbleQr)
              : rawBubbleQr;
          const filteredCards = resolveAssistantMessageHotelCards({
            message: m,
            messageIndex: i,
            selectedHotelFilter,
            hotelFilterAnchorMessageIndex,
            consolidatedCards: consolidatedHotelFilterCards,
          });
          return (
          <React.Fragment key={i}>
            {showHotelFilterBar && i === hotelFilterAnchorMessageIndex ? (
              <div className="hr-cvac__hotel-filter-wrap hr-cc-pub__hotel-filter-wrap">
                <CustomerVacationChatHotelFilterBar
                  availableHotels={availableHotelFilters}
                  selectedHotel={selectedHotelFilter}
                  onChange={setSelectedHotelFilter}
                  statusLine={statusLine}
                  disabled={loading}
                />
              </div>
            ) : null}
            <div
              className={`hr-cc-pub__bubble ${
                m.role === "user" ? "hr-cc-pub__bubble--user" : "hr-cc-pub__bubble--assistant"
              }${m.searchLoading ? " hr-cc-pub__bubble--typing" : ""}${
                m.role === "assistant" && m.passenger_form?.passengers?.length
                  ? " hr-cvac__bubble--with-pax-form"
                  : ""
              }`}
            >
              {m.role === "user" ? (
                displayUserChatMessage(m.text)
              ) : (
                <CustomerVacationChatAssistantText text={m.text || ""} />
              )}
              {m.role === "assistant" ? (
                <CustomerVacationChatPaymentFallback
                  payment={m.payment}
                  client_actions={m.client_actions}
                  disabled={loading}
                  onManualOpenPaymentModal={() => {
                    processChatPaymentManualOpen({
                      sessionId,
                      paymentFromMessage: m.payment,
                      agentId: extractVacationPaymentAgentId(m.client_actions),
                      selectedResult,
                      roomsAndPaxInfo,
                      modalOpenRef: chatPaymentModalOpenRef,
                      traceId: m.trace_id,
                      onOpenModal: () => setChatPaymentModalOpen(true),
                    });
                  }}
                />
              ) : null}
              {m.role === "assistant" && m.package_details_view ? (
                <PackageDetailsChatView
                  view={m.package_details_view}
                  disabled={loading}
                  onPickRoom={(roomIdx) => {
                    const c = packageDetailsViewToCard(m.package_details_view);
                    if (c) {
                      selectPackageDealForChatContext(c, setSelectedResult_resetAllFields);
                    }
                    const rk = Number(m.package_details_view?.offer_rank) || 1;
                    void sendText(`__cvac_select_room__|${rk}|${roomIdx}`);
                  }}
                />
              ) : null}
              {m.role === "assistant" &&
              m.passenger_form &&
              Array.isArray(m.passenger_form.passengers) &&
              m.passenger_form.passengers.length > 0 ? (
                <PassengerFormChatView
                  spec={m.passenger_form}
                  disabled={loading}
                  sessionId={sessionId}
                  onSendControlTurn={(args) => {
                    void sendPassengerControlTurn(args);
                  }}
                />
              ) : null}
              {m.role === "assistant" &&
              m.date_range_picker &&
              i === messages.length - 1 ? (
                <CustomerVacationChatDateRangePicker
                  nightsHint={m.date_range_picker.nights_hint ?? null}
                  pricesByDate={m.date_range_picker.prices_by_date ?? null}
                  disabled={loading}
                  onComplete={(msg) => void sendText(msg)}
                  onDateModeChoice={(msg) => void sendText(msg)}
                />
              ) : null}
              {m.role === "assistant" &&
              m.children_age_picker &&
              i === messages.length - 1 ? (
                <ChildrenAgePicker
                  childrenCount={m.children_age_picker.children_count}
                  preselectedAges={m.children_age_picker.preselected_ages || []}
                  disabled={loading}
                  onConfirm={(ages) => void sendText(ages.join(" "))}
                />
              ) : null}
              {m.role === "assistant" && filteredCards.length ? (
                <div className="hr-cvac__cards">
                  {filteredCards.map((c, j) => (
                    <HotelShortlistCard
                      key={c.offer_rank ?? c.action_key ?? j}
                      card={c}
                      disabled={loading}
                      onSelectOffer={(rank, card) => handleSelectHotelFromCard(rank, card)}
                      onViewPackageDetails={(rank) =>
                        sendFromCard("אשמח לפרטים מלאים על החבילה מהרשימה", {
                          client_action: "view_package_details",
                          offer_rank: rank,
                        })
                      }
                      onPickAlternateDeal={(ldsId) =>
                        sendFromCard(`__cvac_select_lds__|${ldsId}`)
                      }
                    />
                  ))}
                </div>
              ) : null}
              {m.role === "assistant" &&
              bubbleQuickReplies.length > 0 &&
              !m.passenger_form ? (
                <div className="hr-cc-pub__quick">
                  {bubbleQuickReplies.map((qr, k) => (
                    <button
                      key={k}
                      type="button"
                      className="hr-cc-pub__quick-btn"
                      onClick={() => onQuickReply(qr)}
                      disabled={loading}
                    >
                      <CustomerVacationChatQuickReplyLabel qr={qr} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </React.Fragment>
        );
        })}
        {loading && !messages.some((m) => m.searchLoading) ? (
          <div
            className="hr-cvac__pending hr-cvac__bubble hr-cvac__bubble--assistant hr-cvac__bubble--typing"
            aria-live="polite"
          >
            בודק עבורך…
          </div>
        ) : null}
        <div ref={scrollEndRef} className="hr-cvac__scroll-end" aria-hidden />
      </div>

      <div className="hr-cc-pub__composer">
        <input
          className="hr-cc-pub__input"
          type="text"
          placeholder="כתבו הודעה…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading || bootLoading || !sessionId || bootError}
          onKeyDown={(e) => {
            if (e.key === "Enter") void sendText(input);
          }}
        />
        <button
          type="button"
          className="hr-cc-pub__send"
          disabled={loading || bootLoading || !sessionId || bootError || !input.trim()}
          onClick={() => void sendText(input)}
        >
          שליחה
        </button>
      </div>

      <CustomerVacationChatPaymentModal
        open={chatPaymentModalOpen}
        onClose={() => {
          chatPaymentModalOpenRef.current = false;
          setChatPaymentModalOpen(false);
        }}
        agentIdFromSession={null}
      />
    </div>
  );
}
